import { useState } from "react";
import { Send, MessageCircle, Phone, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const WEB3FORMS_KEY = import.meta.env.VITE_WEB3FORMS_KEY as string;

const INITIAL_FORM = {
  name: "",
  company: "",
  email: "",
  phone: "",
  type: "iniciante",
  message: "",
};

const Contact = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const sectionRef = useScrollReveal();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.company) {
      toast.error("Preencha nome, empresa e e-mail antes de enviar.");
      return;
    }

    setSending(true);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject:    `Análise Gratuita — ${formData.company}`,
          from_name:  formData.name,
          email:      formData.email,
          empresa:    formData.company,
          telefone:   formData.phone || "Não informado",
          perfil:     formData.type === "iniciante" ? "Nunca solicitou AFRMM" : "Já solicita AFRMM",
          mensagem:   formData.message || "Sem mensagem adicional.",
        }),
      });

      const data = await res.json();

      if (data.success) {
        toast.success("Mensagem enviada! Entraremos em contato em breve.", { duration: 6000 });
        setFormData(INITIAL_FORM);
      } else {
        throw new Error(data.message);
      }
    } catch {
      toast.error("Falha ao enviar. Tente pelo WhatsApp ou e-mail direto.", { duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  const set = (field: keyof typeof INITIAL_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 16px",
    background: "transparent",
    border: "1px solid rgba(176, 175, 172, 0.25)",
    borderRadius: "4px",
    color: "var(--color-branco-vela)",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 200ms",
    fontFamily: "var(--font-body)",
  };

  const focusBorder = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    ((e.target as HTMLElement).style.borderColor = "var(--color-cinza-nevoa)");
  const blurBorder = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    ((e.target as HTMLElement).style.borderColor = "rgba(176, 175, 172, 0.25)");

  return (
    <section
      id="contato"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-24 relative overflow-hidden bg-grid"
      style={{ background: "var(--color-preto-marca)" }}
    >
      <div className="container mx-auto px-4 lg:px-6 relative">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-animate>
          <div className="eyebrow justify-center" style={{ color: "var(--color-cinza-ancora)" }}>
            Análise Gratuita
          </div>
          <h2 className="heading-h2 mb-4" style={{ color: "var(--color-branco-vela)" }}>
            Pronto para transformar seu AFRMM em investimento real?
          </h2>
          <div className="w-14 h-px mx-auto" style={{ background: "var(--color-cinza-ancora)", opacity: 0.3 }} />
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Form */}
          <div data-animate>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor="contact-name" className="sr-only">Seu nome</label>
                <input
                  id="contact-name"
                  type="text"
                  placeholder="Seu nome *"
                  value={formData.name}
                  onChange={set("name")}
                  onFocus={focusBorder}
                  onBlur={blurBorder}
                  style={inputStyle}
                  required
                />
              </div>

              <div>
                <label htmlFor="contact-company" className="sr-only">Empresa</label>
                <input
                  id="contact-company"
                  type="text"
                  placeholder="Empresa *"
                  value={formData.company}
                  onChange={set("company")}
                  onFocus={focusBorder}
                  onBlur={blurBorder}
                  style={inputStyle}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-email" className="sr-only">E-mail</label>
                  <input
                    id="contact-email"
                    type="email"
                    placeholder="E-mail *"
                    value={formData.email}
                    onChange={set("email")}
                    onFocus={focusBorder}
                    onBlur={blurBorder}
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="sr-only">Telefone</label>
                  <input
                    id="contact-phone"
                    type="tel"
                    placeholder="Telefone"
                    value={formData.phone}
                    onChange={set("phone")}
                    onFocus={focusBorder}
                    onBlur={blurBorder}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Radio */}
              <fieldset>
                <legend className="text-sm mb-3 font-light" style={{ color: "var(--color-cinza-ancora)" }}>
                  Sua empresa:
                </legend>
                <div className="flex flex-wrap gap-4">
                  {[
                    { value: "iniciante", label: "Nunca solicitou AFRMM" },
                    { value: "experiente", label: "Já solicita AFRMM" },
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                      <div
                        className="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors"
                        style={{
                          borderColor: formData.type === option.value
                            ? "var(--color-branco-vela)"
                            : "rgba(176, 175, 172, 0.35)",
                        }}
                      >
                        {formData.type === option.value && (
                          <div className="w-2 h-2 rounded-full" style={{ background: "var(--color-branco-vela)" }} />
                        )}
                      </div>
                      <input
                        type="radio"
                        name="type"
                        value={option.value}
                        checked={formData.type === option.value}
                        onChange={() => setFormData((p) => ({ ...p, type: option.value }))}
                        className="sr-only"
                      />
                      <span className="text-sm font-light" style={{ color: "var(--color-branco-vela)" }}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <label htmlFor="contact-message" className="sr-only">Mensagem</label>
                <textarea
                  id="contact-message"
                  placeholder="Mensagem (opcional)"
                  rows={4}
                  value={formData.message}
                  onChange={set("message")}
                  onFocus={focusBorder}
                  onBlur={blurBorder}
                  style={{ ...inputStyle, resize: "none" }}
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="btn-cta-dark w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={15} />
                    Solicitar Análise Gratuita
                  </>
                )}
              </button>

              <p className="text-xs text-center font-light" style={{ color: "var(--color-cinza-ancora)" }}>
                Sem compromisso. Diagnóstico em 30 minutos.
              </p>
            </form>
          </div>

          {/* Contact Info */}
          <div
            data-animate
            className="md:pl-12 pt-8 md:pt-0 border-t md:border-t-0 md:border-l"
            style={{ borderColor: "rgba(176, 175, 172, 0.12)" }}
          >
            <h3 className="font-bold text-xl mb-6 tracking-tight" style={{ color: "var(--color-branco-vela)" }}>
              Ou fale diretamente
            </h3>

            <div className="space-y-5 mb-8">
              {[
                { href: "tel:+5591981573458", icon: Phone, label: "+55 (91) 98157-3458" },
                { href: "mailto:contato@siatechbrasil.com.br", icon: Mail, label: "contato@siatechbrasil.com.br" },
              ].map(({ href, icon: Icon, label }) => (
                <a
                  key={href}
                  href={href}
                  className="flex items-center gap-3 group cursor-pointer"
                >
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded transition-colors duration-200"
                    style={{
                      background: "rgba(242, 240, 236, 0.04)",
                      border: "1px solid rgba(176, 175, 172, 0.15)",
                    }}
                  >
                    <Icon className="w-4 h-4" style={{ color: "var(--color-cinza-ancora)" }} />
                  </div>
                  <span
                    className="text-sm font-light transition-colors duration-200"
                    style={{ color: "var(--color-cinza-nevoa)" }}
                    onMouseEnter={(e) => ((e.target as HTMLElement).style.color = "var(--color-branco-vela)")}
                    onMouseLeave={(e) => ((e.target as HTMLElement).style.color = "var(--color-cinza-nevoa)")}
                  >
                    {label}
                  </span>
                </a>
              ))}
            </div>

            {/* WhatsApp */}
            <a
              href="https://wa.me/5591981573458?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20os%20servi%C3%A7os%20da%20SIATECH."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 font-semibold text-sm rounded cursor-pointer transition-colors duration-200"
              style={{ background: "#25D366", color: "white" }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = "#20BD5A")}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = "#25D366")}
            >
              <MessageCircle size={17} />
              Conversar pelo WhatsApp
            </a>

            <p className="mt-8 text-sm font-light leading-relaxed" style={{ color: "var(--color-cinza-ancora)" }}>
              Primeira conversa gratuita e sem compromisso. Confirmamos sua elegibilidade em minutos.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
